import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Send,
    Download,
    DollarSign,
    Briefcase,
    FileText,
} from "lucide-react";
import { api, apiErrorMessage, API_BASE } from "@/lib/api";
import {
    StatusBadge,
    money,
    fmtDate,
    fmtDateTime,
    PAYMENT_METHODS,
} from "@/lib/format";

export default function InvoiceDetail() {
    const { id } = useParams();
    const [inv, setInv] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [pay, setPay] = useState({ amount: "", method: "check", note: "" });

    const load = async () => {
        try {
            const { data } = await api.get(`/invoices/${id}`);
            setInv(data.invoice);
            setPay((p) => ({ ...p, amount: data.invoice.balance_due }));
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [id]);

    const send = async () => {
        setBusy(true);
        try {
            await api.post(`/invoices/${id}/send`);
            await load();
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const recordPayment = async (e) => {
        e.preventDefault();
        if (!pay.amount || Number(pay.amount) <= 0) return;
        setBusy(true);
        setErr("");
        try {
            await api.post(`/invoices/${id}/payment`, {
                amount: Number(pay.amount),
                method: pay.method,
                note: pay.note,
            });
            setPay({ amount: "", method: "check", note: "" });
            await load();
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    if (!inv) {
        return (
            <div className="page-shell">
                {err ? (
                    <div
                        className="brand-card"
                        style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}
                    >
                        {err}
                    </div>
                ) : (
                    <p className="text-muted">Loading invoice…</p>
                )}
            </div>
        );
    }

    const isPaid = inv.status === "paid" || inv.balance_due <= 0.005;
    const canSend = ["draft", "sent", "overdue", "partial"].includes(inv.status);

    return (
        <div className="page-shell">
            <Link
                to="/invoices"
                className="link-red inline-flex items-center gap-1 text-sm"
                data-testid="id-back"
            >
                <ArrowLeft size={14} />
                Back to invoices
            </Link>

            <header className="fade-up mt-4 flex items-start justify-between flex-wrap gap-4">
                <div>
                    <p className="eyebrow">Invoice</p>
                    <h1 className="h1 mt-3" data-testid="id-number">
                        {inv.invoice_number}
                    </h1>
                    <div className="flex items-center gap-3 mt-2">
                        <StatusBadge status={inv.status} testId="id-status" />
                        <span className="text-muted text-sm">
                            Issued {fmtDate(inv.created_at)} · Due {fmtDate(inv.due_date)}
                        </span>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <a
                        href={`${API_BASE}/invoices/${id}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-secondary"
                        data-testid="id-pdf"
                    >
                        <Download size={14} />
                        Download PDF
                    </a>
                    {canSend && (
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={send}
                            disabled={busy || !inv.client?.email}
                            title={
                                inv.client?.email
                                    ? "Send to client (email is MOCKED in dev)"
                                    : "Add a client email to enable sending"
                            }
                            data-testid="id-send"
                        >
                            <Send size={14} />
                            {inv.status === "draft" ? "Send" : "Resend"}
                        </button>
                    )}
                    {inv.job_id && (
                        <Link
                            to={`/jobs/${inv.job_id}`}
                            className="btn btn-ghost"
                            data-testid="id-open-job"
                        >
                            <Briefcase size={14} />
                            Job
                        </Link>
                    )}
                    {inv.estimate_id && (
                        <Link
                            to={`/estimates/${inv.estimate_id}`}
                            className="btn btn-ghost"
                            data-testid="id-open-estimate"
                        >
                            <FileText size={14} />
                            Estimate
                        </Link>
                    )}
                </div>
            </header>

            {err && (
                <div
                    className="brand-card mt-4"
                    style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}
                    data-testid="id-error"
                >
                    {err}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
                <div className="lg:col-span-2 flex flex-col gap-5">
                    <div className="brand-card fade-up">
                        <p className="eyebrow">Bill to</p>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <p className="text-xs text-muted">Client</p>
                                <p className="font-display font-bold">{inv.client?.name}</p>
                                <p className="text-sm text-muted">{inv.client?.email || ""}</p>
                                <p className="text-sm text-muted">{inv.client?.phone || ""}</p>
                            </div>
                            <div>
                                <p className="text-xs text-muted">Job</p>
                                <p>{inv.job_number}</p>
                            </div>
                        </div>
                    </div>

                    <div className="brand-card fade-up delay-1">
                        <p className="eyebrow">Line items</p>
                        <div className="overflow-x-auto mt-3">
                            <table className="brand-table" data-testid="id-items">
                                <thead>
                                    <tr>
                                        <th>Description</th>
                                        <th className="text-right">Qty</th>
                                        <th>Unit</th>
                                        <th className="text-right">Price</th>
                                        <th className="text-right">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {inv.line_items?.map((li, i) => (
                                        <tr key={i} style={{ cursor: "default" }}>
                                            <td>{li.description}</td>
                                            <td className="text-right font-mono">
                                                {li.quantity}
                                            </td>
                                            <td>{li.unit?.replace("_", " ")}</td>
                                            <td className="text-right font-mono">
                                                {money(li.unit_price)}
                                            </td>
                                            <td className="text-right font-mono">
                                                {money(li.total)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="mt-4 flex justify-end">
                            <div className="min-w-[260px] space-y-1 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-muted">Subtotal</span>
                                    <span className="font-mono">{money(inv.subtotal)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted">Change orders</span>
                                    <span className="font-mono">
                                        {money(inv.change_orders_total)}
                                    </span>
                                </div>
                                <div className="flex justify-between pt-1 border-t border-[var(--brand-surface)]">
                                    <span className="font-display font-bold">Grand total</span>
                                    <span className="font-mono font-bold">
                                        {money(inv.grand_total)}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted">Paid</span>
                                    <span className="font-mono">- {money(inv.amount_paid)}</span>
                                </div>
                                <div
                                    className="flex justify-between pt-2 mt-1 border-t border-[var(--brand-surface)]"
                                    style={{ fontSize: "1.25rem" }}
                                >
                                    <span className="font-display font-bold">Balance due</span>
                                    <span
                                        className="font-display font-bold font-mono"
                                        style={{
                                            color: isPaid
                                                ? "var(--brand-success)"
                                                : "var(--brand-red)",
                                        }}
                                        data-testid="id-balance"
                                    >
                                        {money(inv.balance_due)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Payments list */}
                    <div className="brand-card fade-up delay-2" data-testid="id-payments">
                        <p className="eyebrow">Payments</p>
                        {inv.payments?.length === 0 && (
                            <p className="text-muted text-sm mt-3" data-testid="id-payments-empty">
                                No payments recorded yet.
                            </p>
                        )}
                        {inv.payments?.length > 0 && (
                            <ul className="mt-3 flex flex-col gap-2">
                                {inv.payments.map((p) => (
                                    <li
                                        key={p._id || p.date}
                                        className="flex items-center justify-between gap-3 p-3 rounded-md"
                                        style={{ background: "var(--brand-cream)" }}
                                    >
                                        <div className="min-w-0">
                                            <p className="font-display font-bold capitalize">
                                                {p.method?.replace("_", " ")}
                                            </p>
                                            <p className="text-xs text-muted">
                                                {fmtDateTime(p.date)}
                                                {p.recorded_by_name
                                                    ? ` · by ${p.recorded_by_name}`
                                                    : ""}
                                                {p.note ? ` · ${p.note}` : ""}
                                            </p>
                                        </div>
                                        <span className="font-mono font-bold">
                                            {money(p.amount)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {/* Right column: record payment */}
                <div className="flex flex-col gap-5">
                    <div className="brand-card fade-up" data-testid="id-record-card">
                        <p className="eyebrow">Record payment</p>
                        {isPaid ? (
                            <p
                                className="mt-3 text-sm"
                                style={{ color: "var(--brand-success)" }}
                                data-testid="id-paid-msg"
                            >
                                Invoice is paid in full
                                {inv.paid_at ? ` (${fmtDate(inv.paid_at)})` : ""}.
                            </p>
                        ) : (
                            <form onSubmit={recordPayment} className="mt-3 flex flex-col gap-3">
                                <div>
                                    <label className="field-label">Amount</label>
                                    <input
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        max={inv.balance_due}
                                        className="field-input"
                                        value={pay.amount}
                                        onChange={(e) =>
                                            setPay({ ...pay, amount: e.target.value })
                                        }
                                        required
                                        data-testid="id-pay-amount"
                                    />
                                </div>
                                <div>
                                    <label className="field-label">Method</label>
                                    <select
                                        className="field-select"
                                        value={pay.method}
                                        onChange={(e) =>
                                            setPay({ ...pay, method: e.target.value })
                                        }
                                        data-testid="id-pay-method"
                                    >
                                        {PAYMENT_METHODS.map((m) => (
                                            <option key={m} value={m} className="capitalize">
                                                {m.replace("_", " ")}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="field-label">Note</label>
                                    <input
                                        className="field-input"
                                        value={pay.note}
                                        onChange={(e) =>
                                            setPay({ ...pay, note: e.target.value })
                                        }
                                        placeholder="Check #1234, deposit, …"
                                        data-testid="id-pay-note"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={busy}
                                    data-testid="id-pay-submit"
                                >
                                    <DollarSign size={14} />
                                    {busy ? "Saving…" : "Record payment"}
                                </button>
                            </form>
                        )}
                    </div>

                    <div className="brand-card fade-up delay-1" data-testid="id-meta">
                        <p className="eyebrow">QuickBooks export</p>
                        <p className="text-sm mt-3">
                            <span
                                className={`badge-pill ${inv.quickbooks_ready ? "badge-success" : "badge-warning"}`}
                            >
                                {inv.quickbooks_ready ? "ready" : "not ready"}
                            </span>
                        </p>
                        <p className="text-xs text-muted mt-3">
                            Line items, change orders, and payments are structured for export. A
                            dedicated export endpoint will ship in a later phase.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
